import { Box } from './kit'
import { box, cylinder, line, P, poly } from './projection'

/* ==========================================================================
   Assembly tooling: the housing (an ISOFIX bezel) on a shuttle nest, drawn
   in section — everything in front of z 30 cut away, cut faces hatched 45° —
   so the cap's snap hooks are seen riding the lead-in chamfer and clicking
   into the undercut windows; the cap's hooks; the spring-loaded inspection
   probe under the beam. The cap itself is the pad film's (padParts.jsx).
   World units (projection.js).
   ========================================================================== */

/* ---- Shuttle rail and nest (the nest moves with `nest-x`) ----------- */

const RAIL = box({ x0: 980, x1: 1400, z0: 66, z1: 94, h0: 0, h1: 10 })
const PLATE = box({ x0: 990, x1: 1240, z0: 0, z1: 160, h0: 10, h1: 26 })
const PLATE_SCREWS = [1004, 1226].map((x) => `M${x + 2.5} 682a2.5 2.5 0 1 0 -5 0a2.5 2.5 0 1 0 5 0`).join('')

export function ShuttleRail() {
  return (
    <g>
      <path className="f-poly" d={RAIL.shadow} />
      <Box geo={RAIL} mat="d" />
    </g>
  )
}

/* ---- The housing in section ----------------------------------------------
   Outer x 1016..1204, z 30 (cut)..150, h 26..80. Pocket x 1047..1173, floor
   h 36, back wall z 136; a 5-unit lead-in chamfer round the rim; undercut
   windows h 52..62, 6 deep, in the side walls where the hooks catch. */
const HOUSING = box({ x0: 1016, x1: 1204, z0: 30, z1: 150, h0: 26, h1: 80 })
const H_TOP = poly([
  P(1016, 80, 30),
  P(1042, 80, 30),
  P(1042, 80, 141),
  P(1178, 80, 141),
  P(1178, 80, 30),
  P(1204, 80, 30),
  P(1204, 80, 150),
  P(1016, 80, 150),
])
const FLOOR = poly([P(1047, 36, 30), P(1173, 36, 30), P(1173, 36, 136), P(1047, 36, 136)])
const BACK_WALL = poly([P(1047, 75, 136), P(1173, 75, 136), P(1173, 36, 136), P(1047, 36, 136)])
const LEFT_WALL = poly([P(1047, 75, 30), P(1047, 75, 136), P(1047, 36, 136), P(1047, 36, 30)])
const WINDOW = poly([P(1047, 62, 30), P(1047, 62, 52), P(1047, 52, 52), P(1047, 52, 30)])
/* What the cut shows inside the windows: the left one's back and floor, the right one's end and floor. */
const WINDOW_DEPTHS = [
  poly([P(1041, 62, 30), P(1041, 62, 52), P(1041, 52, 52), P(1041, 52, 30)]),
  poly([P(1173, 62, 52), P(1179, 62, 52), P(1179, 52, 52), P(1173, 52, 52)]),
].join('')
const WINDOW_FLOORS = [1041, 1173]
  .map((x) => poly([P(x, 52, 30), P(x + 6, 52, 30), P(x + 6, 52, 52), P(x, 52, 52)]))
  .join('')
const CHAMFER_L = poly([P(1042, 80, 30), P(1047, 75, 30), P(1047, 75, 136), P(1042, 80, 141)])
const CHAMFER_R = poly([P(1178, 80, 30), P(1178, 80, 141), P(1173, 75, 136), P(1173, 75, 30)])
const CHAMFER_B = poly([P(1042, 80, 141), P(1178, 80, 141), P(1173, 75, 136), P(1047, 75, 136)])
const CUT_OUTLINE = [
  [1016, 26],
  [1204, 26],
  [1204, 80],
  [1178, 80],
  [1173, 75],
  [1173, 62],
  [1179, 62],
  [1179, 52],
  [1173, 52],
  [1173, 36],
  [1047, 36],
  [1047, 52],
  [1041, 52],
  [1041, 62],
  [1047, 62],
  [1047, 75],
  [1042, 80],
  [1016, 80],
]
const CUT = poly(CUT_OUTLINE.map(([x, h]) => P(x, h, 30)))

export function Nest({ id }) {
  return (
    <g className="pp-nest">
      <path className="f-poly" d={PLATE.shadow} />
      <Box geo={PLATE} />
      <path className="f-gR pp-hair" d={PLATE_SCREWS} />
      <path className="f-aF" d={HOUSING.shadow} />
      <path className="f-pS" d={HOUSING.side} />
      <path className="f-pR" d={FLOOR} />
      <path className="f-pW" d={BACK_WALL} />
      <path className="f-pS" d={LEFT_WALL} />
      <path className="f-poly" d={`${WINDOW}${WINDOW_DEPTHS}`} />
      <path className="f-pR" d={WINDOW_FLOORS} />
      <path className="f-pD" d={CHAMFER_L} />
      <path className="f-pL" d={CHAMFER_R} />
      <path className="f-pL" d={CHAMFER_B} />
      <path className="f-pF" d={H_TOP} />
      <path d={H_TOP} fill={`url(#${id}stipple)`} />
      <path className="l-glint" d={line([P(1016, 80, 150), P(1204, 80, 150), P(1204, 80, 30)])} />
      <path className="f-pW" d={CUT} />
      <path d={CUT} fill={`url(#${id}hatch)`} />
      <CheckLamp />
    </g>
  )
}

/* ---- Snap hooks on the cap (pad coordinates: cap bottom at h 58) --------
   Cantilevers 8 thick and 40 long hang from inside the cap at its front
   corners (z 30..46), each authored at its root so it deflects about it
   (`hook`); the barb stands 5 proud, its catch face 30 below the root.
   Their front faces lie on the section plane, hatched the other way (-45°,
   the adjacent part) by mirroring the hatch. */
const HOOKS = {
  l: {
    at: 'translate(1057.4 615)',
    face: 'M-4 0H4V40H-4V38L-9 30H-4Z',
    mirrored: 'M4 0H-4V40H4V38L9 30H4Z',
    side: 'M4 0L6.88 -8V32L4 40Z',
  },
  r: {
    at: 'translate(1173.4 615)',
    face: 'M-4 0H4V30H9L4 38V40H-4Z',
    mirrored: 'M4 0H-4V30H-9L-4 38V40H4Z',
    side: 'M4 0L6.88 -8V22L4 30ZM4 30H9L11.88 22H6.88Z',
  },
}

function Hook({ id, side }) {
  const hook = HOOKS[side]
  return (
    <g transform={hook.at}>
      <g className={`pp-hook-${side}`}>
        <path className="f-pS" d={hook.side} />
        <path className="f-pL" d={hook.face} />
        <g transform="scale(-1 1)">
          <path d={hook.mirrored} fill={`url(#${id}hatch)`} />
        </g>
      </g>
    </g>
  )
}

/** Both hooks, clipped where the supply fixture's nest hides them. */
export function Hooks({ id }) {
  return (
    <g clipPath={`url(#${id}hooks)`}>
      <g className="pp-cap-carry">
        <Hook id={id} side="l" />
        <Hook id={id} side="r" />
      </g>
    </g>
  )
}

/* ---- Inspection probe: a fixed cylinder under the beam (z 80), its rod,
   a spring-loaded pin (the spring takes the last of the stroke) ---------- */
const PROBE = cylinder(16, 128, [[0, 0.24], [0.7, 1]])
const SPRING = `M0 0${Array.from({ length: 12 }, (_, i) => `L${i % 2 ? -7 : 7} ${2.5 * (i + 1)}`).join('')}L0 30`

function Chrome({ x, y0, y1, w }) {
  return (
    <>
      <rect className="f-cB" x={x - w / 2} y={y0} width={w} height={y1 - y0} />
      <rect className="f-cA" x={x - w / 2} y={y0} width={w * 0.22} height={y1 - y0} />
      <rect className="f-cC" x={x + w * 0.15} y={y0} width={w * 0.35} height={y1 - y0} />
    </>
  )
}

/* The probe stands at x 1324: its pin lands on the cap face right of the
   mark (the nest shuttles the part 150), never on the fresh print. */
export function ProbeBody() {
  return (
    <g transform="translate(1324 320)">
      <path className="f-aF" d={PROBE.body} />
      <path className="f-aT" d={PROBE.bands[0]} />
      <path className="f-aS" d={PROBE.bands[1]} />
      <path className="l-aS pp-hair" d={PROBE.foot} />
    </g>
  )
}

export function ProbeMover() {
  return (
    <g>
      <g className="pp-probe-rod">
        <Chrome x={0} y0={0} y1={1} w={8} />
      </g>
      <g className="pp-probe-head">
        <rect className="f-aS" x="1338" y="338" width="4" height="18" />
        <rect className="f-aF" x="1310" y="340" width="28" height="16" />
        <rect className="f-aT" x="1310" y="336" width="32" height="4" />
        <path className="l-spec" d="M1310 356V340H1338" />
      </g>
      <g className="pp-spring">
        <path className="l-spring" d={SPRING} />
      </g>
      <g className="pp-probe-pin">
        <rect className="f-aS" x="1316" y="386" width="16" height="6" />
        <Chrome x={1324} y0={392} y1={428} w={6} />
        <path className="f-cA" d="M1321 428A3 6 0 0 0 1327 428Z" />
      </g>
    </g>
  )
}

/* ---- Check lamp, click ticks, centre axis -------------------------------- */

/* A small moulded indicator on the nest's front corner: the check lights in
   it once the probe has passed the part (it rides the nest, `nest-x`). */
const LAMP = box({ x0: 1214, x1: 1236, z0: 4, z1: 24, h0: 26, h1: 48 })

function CheckLamp() {
  return (
    <g>
      <Box geo={LAMP} mat="p" />
      <rect className="l-frame" x="1217.7" y="652.5" width="16" height="16" rx="4.2" />
      <g className="pp-check">
        <g transform="translate(1217.7 652.5) scale(0.667)">
          <path className="l-check" d="m5 12.5 4.5 4.5L19 7.5" />
        </g>
      </g>
    </g>
  )
}

/** Three short ticks at each lip when the hooks click in (seated, before the shuttle). */
export function ClickTicks() {
  return (
    <g className="pp-ticks">
      <g transform="translate(1040 628)">
        <path className="l-knock pp-tick" d="M-2 -6l-12 -7M-3 0h-15M-2 6l-12 7" />
        <path className="l-tick pp-tick" d="M-2 -6l-12 -7M-3 0h-15M-2 6l-12 7" />
      </g>
      <g transform="translate(1191 628)">
        <path className="l-knock pp-tick" d="M2 -6l12 -7M3 0h15M2 6l12 7" />
        <path className="l-tick pp-tick" d="M2 -6l12 -7M3 0h15M2 6l12 7" />
      </g>
    </g>
  )
}

export function Axis() {
  return <path className="l-axis pp-axis-line" d="M1124.4 352V676" />
}
